import { useCallback, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { any, object, string, TypeOf, z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { updatePeople } from "../../redux/actions/people.actions";
import { useDispatch, useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import SaveIcon from "@mui/icons-material/Save";
import { green } from "@mui/material/colors";

import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

interface Props {
  isDialogOpened: boolean;
  handleCloseDialog: any;
  action: string;
}
const formSchema = object({
  id: any(),
  fullname: z.string().nonempty("Full name is required"),
  address: string().nonempty("Address is required"),
  email: string()
    .nonempty("Email address is required")
    .regex(/^(\s?[^\s,]+@[^\s,]+\.[^\s,]+\s?,)*(\s?[^\s,]+@[^\s,]+\.[^\s,]+)$/),
  mobile_number: string().nonempty("Mobile number number is required"),
  date_of_birth: z.any(z.instanceof(File)).optional(),
  status: any(),
  role: any(),
  username: string(),
});
type FormInput = TypeOf<typeof formSchema>;

const defaultValues = {
  fullname: "",
  address: "",
  email: "",
  date_of_birth: "",
  is_staff: "",
  is_superuser: "",
  is_active: "",
};

const roleList = [
  {
    id: 1,
    name: "Super Admin",
  },
  {
    id: 2,
    name: "Staff",
  },
  {
    id: 3,
    name: "Registered User",
  },
];

const statusList = [
  {
    id: 1,
    name: "Active",
  },
  {
    id: 2,
    name: "InActive",
  },
];

export function PeopleForm(props: Props) {
  const { handleCloseDialog } = props;
  const dispatch = useDispatch();
  const { user, save } = useSelector((state: any) => state.people);
  const loading = useSelector((state: any) => state.UI.loading);

  const {
    register,
    reset,
    control,
    setValue,
    formState: { errors },
    handleSubmit,
  } = useForm<FormInput>({ resolver: zodResolver(formSchema) });
  // let imagePath: string | undefined = process.env.REACT_APP_ENDPOINT;
  // imagePath = imagePath ? imagePath.replace(/\/+$/, "") : "";

  const handleClose = useCallback(() => {
    handleCloseDialog(false);
    reset(defaultValues);
  }, [reset, handleCloseDialog]);

  useEffect(() => {
    if (save) {
      handleClose();
    }
  }, [save, handleClose]);

  useEffect(() => {
    if (user) {
      if (user.is_superuser) user.role = 1;
      else if (user.is_staff) user.role = 2;
      else user.role = 3;

      user.status = user.is_active ? 1 : 2;
      register("username", { required: true });
      setValue("username", user.username);
    }
    reset(user);
  }, [user, reset, register, setValue]);

  const onFormSubmit: SubmitHandler<FormInput> = (data) => {
    const formData = new FormData();
    formData.append("fullname", data.fullname);
    formData.append("address", data.address);
    formData.append("email", data.email);
    formData.append("mobile_number", data.mobile_number);
    formData.append("date_of_birth", data.date_of_birth);
    formData.append("is_superuser", data.role === 1 ? "1" : "0");
    formData.append("is_staff", data.role === 3 ? "0" : "1");
    formData.append("is_active", data.status === 2 ? "0" : "1");
    formData.append("username", data.username);

    if (data.id) {
      formData.append("id", data.id);
      dispatch<any>(updatePeople(formData, data.id));
    }
  };

  return (
    <Dialog open={props.isDialogOpened} onClose={handleClose} fullWidth>
      <DialogTitle>{props.action}</DialogTitle>
      <DialogContent>
        <Box
          component="form"
          id="peopleform"
          name="peopleform"
          noValidate
          onSubmit={handleSubmit(onFormSubmit)}
        >
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Full Name"
            fullWidth
            variant="standard"
            required
            {...register("fullname")}
            error={!!errors["fullname"]}
            helperText={errors["fullname"] ? errors["fullname"].message : ""}
          />
          <TextField
            margin="dense"
            id="name"
            label="Address"
            fullWidth
            variant="standard"
            required
            {...register("address")}
            error={!!errors["address"]}
            helperText={errors["address"] ? errors["address"].message : ""}
          />
          <TextField
            margin="dense"
            id="name"
            label="Mobile Number"
            fullWidth
            variant="standard"
            required
            {...register("mobile_number")}
            error={!!errors["mobile_number"]}
            helperText={
              errors["mobile_number"] ? errors["mobile_number"].message : ""
            }
          />
          <TextField
            margin="dense"
            id="name"
            label="Email Address"
            fullWidth
            variant="standard"
            required
            {...register("email")}
            error={!!errors["email"]}
            helperText={errors["email"] ? errors["email"].message : ""}
          />

          <TextField
            sx={{ mt: 1 }}
            id="date_of_birth"
            label="Birthday"
            type="date"
            required
            fullWidth
            variant="standard"
            InputLabelProps={{
              shrink: true,
            }}
            error={!!errors["date_of_birth"]}
            {...register("date_of_birth")}
            helperText={
              errors["date_of_birth"] ? errors["date_of_birth"].message : ""
            }
          />

          <FormControl fullWidth variant="standard" sx={{ mt: 2 }}>
            <InputLabel id="role-select-label" className="mb-2">
              Role *{errors["role"] && <span>{errors["role"].message}</span>}
            </InputLabel>
            <Controller
              name="role"
              control={control}
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Select
                  defaultValue="1"
                  labelId="Role"
                  className="input-container"
                  id="Role"
                  size="small"
                  disabled={!roleList?.length}
                  value={value}
                  onChange={onChange}
                >
                  {roleList.map((c, i) => (
                    <MenuItem key={`c-${i}`} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>
          <FormControl
            fullWidth
            className="custom_select"
            variant="standard"
            sx={{ mt: 2 }}
          >
            <InputLabel id="status-select-label" className="mb-2">
              Status *
            </InputLabel>
            <Controller
              name="status"
              control={control}
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Select
                  defaultValue="1"
                  labelId="Status"
                  className="input-container"
                  id="status"
                  size="small"
                  disabled={!statusList?.length}
                  value={value}
                  onChange={onChange}
                >
                  {statusList.map((c, i) => (
                    <MenuItem key={`c-${i}`} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>

          <DialogActions>
            <Box sx={{ m: 1, position: "relative" }}>
              <Button
                type="submit"
                form="peopleform"
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                Save
              </Button>
              {loading && (
                <CircularProgress
                  size={24}
                  sx={{
                    color: green[500],
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
            <Button
              onClick={handleClose}
              variant="contained"
              color="error"
              disabled={loading}
            >
              Cancel
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
